import React, { useEffect, useRef, useState } from 'react'

import clsx from 'clsx'
import ReactDOM from 'react-dom'

import { CardActionArea } from 'components/Card'
import Grid from 'components/Grid'
import Lightbox from 'components/Lightbox'
import MediaCarousel from 'components/MediaCarousel'
import { FixedImage } from 'components/NextImage'
import { useEventContext } from 'context/EventContext'
import { isClient } from 'helpers/canUse'
import { convertToSlug } from 'helpers/resources'
import useViewport from 'hooks/useViewport'

import classes from './Screenshots.module.scss'

const SIZES = {
  default: {
    main: { spacing: 12 },
    carousel: { spacing: 12 },
  },
  vertical: {
    main: { spacing: 10 },
    carousel: { spacing: 2 },
  },
}

const getLightBoxProps = screenshots => {
  const captions = []
  const sources = []
  screenshots.forEach(screenshot => {
    captions.push(screenshot.caption)
    sources.push(screenshot.embed_url || screenshot.url)
  })

  return {
    captions,
    sources,
  }
}

/** @type {import("./types").default} */
const Screenshots = ({
  carouselImageProps = {},
  className,
  forceArrows = false,
  forceMainImage = false,
  mainImageClassName,
  mainImageProps = {},
  mediaClassName,
  screenshots,
  variant = 'horizontal',
}) => {
  const { viewport, is, up } = useViewport()
  const [carouselScreenshots, setCarouselScreenshots] = useState([])
  const [lightboxState, setLightboxState] = useState({ toggler: false })
  const showMainImage = forceMainImage || is('xs')

  const mainImageSpacing = variant === 'horizontal' ? SIZES.default.main.spacing : SIZES.vertical.main.spacing
  const carouselSpacing = variant === 'horizontal' ? SIZES.default.carousel.spacing : SIZES.vertical.carousel.spacing

  const { sendEvent } = useEventContext({})

  const mediaIndex = useRef(1)
  useEffect(() => {
    setCarouselScreenshots(showMainImage ? screenshots.slice(1) : screenshots)
  }, [viewport, screenshots, is, showMainImage])

  if (screenshots.length === 0) {
    return null
  }
  const mainImage = screenshots[0]

  const handleOnClose = () => {
    sendEvent({
      engagementComponent: 'product_media',
      engagementDestination: 'close-media',
      engagementDetails: 'media',
      name: 'engagement_product_click',
    })
  }

  const lightboxToggleHandler = index => () => {
    mediaIndex.current = showMainImage ? index + 2 : index + 1
    sendEvent({
      engagementComponent: 'product_media',
      engagementDestination: 'open-media',
      engagementDetails: 'media',
      name: 'engagement_product_click',
    })

    setLightboxState({
      toggler: !lightboxState.toggler,
    })
  }

  return (
    <>
      <Grid container className={clsx('MediaCarousel', className)} spacing={is('xs') ? 1 : 2}>
        {showMainImage && (
          <Grid data-testid="mainImageContainer" item xs={mainImageSpacing}>
            <CardActionArea onClick={lightboxToggleHandler(-1)} className={classes.imageContainer}>
              <FixedImage
                fetchWidth={750}
                fetchHeight={450}
                src={mainImage.url}
                alt={mainImage.title}
                className={clsx(classes.mainImage, mainImageClassName)}
                loading={up('md') ? 'eager' : 'lazy'}
                fetchpriority={up('md') ? 'high' : 'auto'}
                {...mainImageProps}
              />
            </CardActionArea>
          </Grid>
        )}

        {carouselScreenshots.length > 0 && (
          <Grid item xs={carouselSpacing}>
            <MediaCarousel.Carousel orientation={variant} forceArrows={forceArrows}>
              {carouselScreenshots.map((screenshot, i) => (
                <MediaCarousel.Media
                  className={mediaClassName}
                  screenshot={screenshot}
                  orientation={variant}
                  key={`image-${i}-${screenshot.caption ? convertToSlug(screenshot.caption) : ''}`}
                  loading="lazy"
                  onClick={lightboxToggleHandler(i)}
                  imageProps={carouselImageProps}
                />
              ))}
            </MediaCarousel.Carousel>
          </Grid>
        )}
      </Grid>
      {isClient &&
        ReactDOM.createPortal(
          <Lightbox
            slide={mediaIndex.current}
            onClose={handleOnClose}
            toggler={lightboxState.toggler}
            {...getLightBoxProps(screenshots)}
          />,
          document?.getElementById('portal_root') || document.body
        )}
    </>
  )
}

export default Screenshots
